import React, { ReactNode } from "react";

import Box from "veneer/Box";
import Stack from "veneer/Stack";
import styled from "veneer/styled";
import { Colors } from "veneer/Theme";
import Typography from "veneer/Typography";

interface ParameterSectionDataTestId {
  description?: string;
  root?: string;
  title?: string;
}

interface ParameterSectionProps {
  action?: ReactNode;
  children?: ReactNode;
  dataTestId?: ParameterSectionDataTestId;
  description?: ReactNode;
  disabled?: boolean;
  footer?: ReactNode;
  input?: ReactNode;
  title?: ReactNode;
}

const InputContainer = styled(Box)({
  "& .MuiInputBase-root": {
    backgroundColor: Colors.white,
  },
});

function renderContent(
  content: ReactNode,
  dataTestId: string | undefined,
  variant: "body1" | "body2",
  color?: string,
) {
  if (typeof content === "string") {
    return (
      <Typography color={color} dataTestId={dataTestId} variant={variant}>
        {content}
      </Typography>
    );
  }

  return content;
}

const ParameterSection = ({
  action,
  children,
  dataTestId,
  description,
  disabled = false,
  footer,
  input,
  title,
}: ParameterSectionProps) => {
  const hasBody = Boolean(title || description || action);
  const hasContentAfterInput = Boolean(hasBody || children || footer);

  return (
    <Box
      bgcolor={Colors.gray100}
      borderRadius={2}
      dataTestId={dataTestId?.root}
      p={2}
    >
      {input && (
        <InputContainer mb={hasContentAfterInput ? 1 : 0}>
          {input}
        </InputContainer>
      )}
      {hasBody && (
        <Box
          alignItems="start"
          display="flex"
          gap={2}
          justifyContent="space-between"
        >
          <Stack gap={0.5}>
            {renderContent(
              title,
              dataTestId?.title,
              "body1",
              disabled ? Colors.black38 : Colors.gray700,
            )}
            {renderContent(description, dataTestId?.description, "body2")}
          </Stack>
          {action}
        </Box>
      )}
      {children && <Box mt={hasBody || input ? 1 : 0}>{children}</Box>}
      {footer && <Box mt={hasBody || input || children ? 1 : 0}>{footer}</Box>}
    </Box>
  );
};

export { ParameterSection };
